// Minimal EXIF reader for JPEG photos added from this device: when the photo was taken
// (DateTimeOriginal) and where (GPS). Everything else in EXIF is ignored. Any malformed
// or missing data gives nulls rather than an error.

export interface PhotoMeta {
  /** Camera wall-clock time, "YYYY-MM-DDTHH:mm:ss". */
  takenAt: string | null;
  latitude: number | null;
  longitude: number | null;
}

const NONE: PhotoMeta = { takenAt: null, latitude: null, longitude: null };

/** EXIF sits near the start of a JPEG; this much is always enough. */
export const EXIF_SCAN_BYTES = 256 * 1024;

export function readExif(buf: ArrayBuffer): PhotoMeta {
  try {
    const tiff = findTiff(new DataView(buf));
    return tiff ? readTiff(tiff) : NONE;
  } catch {
    return NONE;
  }
}

/** Walks the JPEG markers to the APP1 "Exif" segment; returns a view of its TIFF block. */
function findTiff(v: DataView): DataView | null {
  if (v.byteLength < 4 || v.getUint16(0) !== 0xffd8) return null;
  let p = 2;
  while (p + 4 <= v.byteLength) {
    if (v.getUint8(p) !== 0xff) return null;
    const marker = v.getUint8(p + 1);
    if (marker === 0xda || marker === 0xd9) return null; // image data starts: no EXIF
    const len = v.getUint16(p + 2);
    if (marker === 0xe1 && p + 10 <= v.byteLength && v.getUint32(p + 4) === 0x45786966 && v.getUint16(p + 8) === 0) {
      const start = p + 10;
      const end = Math.min(p + 2 + len, v.byteLength);
      return new DataView(v.buffer, v.byteOffset + start, end - start);
    }
    p += 2 + len;
  }
  return null;
}

function readTiff(t: DataView): PhotoMeta {
  const order = t.getUint16(0);
  if (order !== 0x4949 && order !== 0x4d4d) return NONE;
  const le = order === 0x4949;
  const u16 = (o: number) => t.getUint16(o, le);
  const u32 = (o: number) => t.getUint32(o, le);
  if (u16(2) !== 42) return NONE;

  /** Tag → offset of its 12-byte entry. */
  const ifd = (offset: number): Map<number, number> => {
    const m = new Map<number, number>();
    if (!offset || offset + 2 > t.byteLength) return m;
    const n = u16(offset);
    for (let i = 0; i < n; i++) {
      const e = offset + 2 + i * 12;
      if (e + 12 > t.byteLength) break;
      m.set(u16(e), e);
    }
    return m;
  };
  /** Where an entry's value lives: inline when it fits in 4 bytes, else at the offset. */
  const valueAt = (e: number, bytes: number) => (bytes <= 4 ? e + 8 : u32(e + 8));
  const ascii = (e: number | undefined): string | null => {
    if (e === undefined || u16(e + 2) !== 2) return null;
    const count = u32(e + 4);
    const at = valueAt(e, count);
    if (at + count > t.byteLength) return null;
    let s = '';
    for (let i = 0; i < count; i++) {
      const c = t.getUint8(at + i);
      if (!c) break;
      s += String.fromCharCode(c);
    }
    return s;
  };
  /** Three unsigned rationals (degrees, minutes, seconds) → decimal degrees. */
  const dms = (e: number | undefined): number | null => {
    if (e === undefined || u16(e + 2) !== 5 || u32(e + 4) < 3) return null;
    const at = u32(e + 8);
    if (at + 24 > t.byteLength) return null;
    const r = (i: number) => {
      const den = u32(at + i * 8 + 4);
      return den ? u32(at + i * 8) / den : 0;
    };
    return r(0) + r(1) / 60 + r(2) / 3600;
  };

  const ifd0 = ifd(u32(4));
  const exifPtr = ifd0.get(0x8769);
  const gpsPtr = ifd0.get(0x8825);
  const exif = exifPtr !== undefined ? ifd(u32(exifPtr + 8)) : new Map<number, number>();
  const takenAt = exifDate(ascii(exif.get(0x9003)) ?? ascii(ifd0.get(0x0132)));

  let latitude: number | null = null;
  let longitude: number | null = null;
  if (gpsPtr !== undefined) {
    const gps = ifd(u32(gpsPtr + 8));
    const lat = dms(gps.get(2));
    const lon = dms(gps.get(4));
    if (lat !== null && lon !== null && lat <= 90 && lon <= 180 && (lat !== 0 || lon !== 0)) {
      latitude = ascii(gps.get(1))?.toUpperCase() === 'S' ? -lat : lat;
      longitude = ascii(gps.get(3))?.toUpperCase() === 'W' ? -lon : lon;
    }
  }
  return { takenAt, latitude, longitude };
}

/** "2019:07:14 10:22:31" → "2019-07-14T10:22:31"; blank or zeroed dates → null. */
export function exifDate(raw: string | null): string | null {
  const m = raw?.match(/^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
  if (!m || m[1] === '0000' || m[2] === '00') return null;
  return `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}`;
}
